/**
 * Hero — Premium full-bleed background layout
 * Features deep dark gradient overlay on left for absolute legibility,
 * and high-contrast editorial typography.
 */
import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaArrowRight, FaFemale } from 'react-icons/fa';
import { gsap } from 'gsap';
import bannerImg from '../../assets/images/Banner.png';

const Hero = () => {
  const heroRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo('.hero-tag',
        { y: 15, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out', delay: 0.1 }
      );
      gsap.fromTo('.hero-title-line',
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, stagger: 0.08, duration: 0.7, ease: 'power3.out', delay: 0.25 }
      );
      gsap.fromTo('.hero-desc',
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: 'power2.out', delay: 0.55 }
      );
      gsap.fromTo('.hero-actions',
        { y: 15, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out', delay: 0.75 }
      );
      gsap.fromTo('.hero-stats-bar',
        { y: 30, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out', delay: 0.95 }
      );
    }, heroRef);
    return () => ctx.revert();
  }, []);

  const stats = [
    { value: '24 / 7', label: 'Support Operations' },
    { value: 'India',  label: 'Subcontinent Wide' },
    { value: 'Global', label: 'Agency Network' },
    { value: '100%',   label: 'Client Retained' },
  ];

  return (
    <section ref={heroRef} style={{
      position: 'relative',
      width: '100%',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      paddingTop: '120px',
      paddingBottom: '120px',
      background: 'var(--navy-deep)',
      overflow: 'hidden'
    }}>
      {/* Full-bleed background image */}
      <div style={{
        position: 'absolute',
        inset: 0,
        zIndex: 1
      }}>
        <img
          src={bannerImg}
          alt="ISAAC Logistic Solutions - Shipping & Logistics Company in Thoothukudi VOC Port"
          loading="eager"
          fetchPriority="high"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
        />
        {/* Soft, dark gradient overlay to ensure high readability on the left */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(to right, rgba(3, 6, 17, 0.95) 0%, rgba(3, 6, 17, 0.8) 45%, rgba(3, 6, 17, 0.3) 100%)',
          pointerEvents: 'none'
        }} />
      </div>

      {/* Decorative vertical lines on top of image for Awwwards layout rhythm */}
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: '10%', width: '1px',
        background: 'rgba(255,255,255,0.03)', pointerEvents: 'none', zIndex: 2
      }} />
      <div style={{
        position: 'absolute', top: 0, bottom: 0, left: '50%', width: '1px',
        background: 'rgba(255,255,255,0.03)', pointerEvents: 'none', zIndex: 2
      }} />

      <div className="container-xxl px-4 px-lg-5" style={{ position: 'relative', zIndex: 3 }}>
        <div className="row">
          <div className="col-lg-8 text-start">
            {/* Elegant glassmorphic badge */}
            <div className="hero-tag" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: 'rgba(106, 27, 154, 0.15)',
              border: '1px solid rgba(106, 27, 154, 0.35)',
              borderRadius: '50px',
              padding: '0.45rem 1.1rem',
              marginBottom: '1.5rem',
              backdropFilter: 'blur(10px)',
              opacity: 0
            }}>
              <FaFemale style={{ color: 'var(--purple-accent-light)', fontSize: '0.9rem' }} />
              <span style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '0.72rem',
                fontWeight: 700,
                color: '#fff',
                letterSpacing: '1.2px',
                textTransform: 'uppercase'
              }}>
                Women-Owned & Led Entity
              </span>
            </div>

            {/* Editorial typography */}
            <h1 className="hero-title" style={{
              color: '#FFFFFF',
              lineHeight: 1.08,
              marginBottom: '1.5rem',
              letterSpacing: '-0.025em',
              fontWeight: 800
            }}>
              <span className="hero-title-line d-block" style={{ opacity: 0 }}>Connecting</span>
              <span className="hero-title-line d-block" style={{ opacity: 0 }}>Possibilities.</span>
              <span className="hero-title-line d-block" style={{ opacity: 0 }}>
                Delivering <span style={{ color: 'var(--orange)' }}>Excellence.</span>
              </span>
            </h1>

            {/* Sub-narrative */}
            <p className="hero-desc" style={{
              opacity: 0,
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '1.02rem',
              lineHeight: 1.8,
              marginBottom: '2.8rem',
              maxWidth: 580
            }}>
              ISAAC Logistic Solutions India Pvt. Ltd. — a women lead organisation
              delivering integrated shipping, logistics and marine solutions with
              excellence, integrity and global standards. We anchor operations with absolute precision.
            </p>

            {/* Action buttons */}
            <div className="hero-actions d-flex flex-wrap gap-3 mb-5" style={{ opacity: 0 }}>
              <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}>
                <Link to="/services" style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.65rem',
                  background: 'var(--orange)',
                  color: '#fff',
                  border: '1px solid var(--orange)',
                  fontFamily: 'var(--font-heading)', fontWeight: 700,
                  fontSize: '0.8rem', padding: '1rem 2.2rem', borderRadius: 2,
                  textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '1.5px',
                  transition: 'all 0.25s ease'
                }} className="btn-hero-primary">
                  Explore Divisions <FaArrowRight size={10} />
                </Link>
              </motion.div>
              
              <motion.div whileHover={{ y: -1 }} whileTap={{ scale: 0.98 }}>
                <Link to="/contact" style={{
                  display: 'inline-flex', alignItems: 'center',
                  background: 'transparent',
                  color: '#fff',
                  border: '1px solid rgba(255,255,255,0.25)',
                  fontFamily: 'var(--font-heading)', fontWeight: 600,
                  fontSize: '0.8rem', padding: '1rem 2.2rem', borderRadius: 2,
                  textDecoration: 'none', textTransform: 'uppercase', letterSpacing: '1.5px',
                  transition: 'all 0.25s ease'
                }} className="btn-hero-secondary">
                  Partner With Us
                </Link>
              </motion.div>
            </div>

            {/* Translucent bottom stats strip (Premium glassmorphism) */}
            <div className="hero-stats-bar" style={{
              opacity: 0,
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.06)',
              borderRadius: 2,
              padding: '1.5rem 2rem',
              backdropFilter: 'blur(10px)'
            }}>
              <div className="row g-4">
                {stats.map((s, i) => (
                  <div key={i} className="col-6 col-md-3">
                    <div style={{
                      fontFamily: 'var(--font-heading)',
                      fontWeight: 700,
                      fontSize: '1.5rem',
                      color: 'var(--orange)',
                      lineHeight: 1,
                      marginBottom: '0.3rem'
                    }}>
                      {s.value}
                    </div>
                    <div style={{
                      fontSize: '0.62rem',
                      color: 'rgba(255, 255, 255, 0.45)',
                      letterSpacing: '1.5px',
                      textTransform: 'uppercase',
                      fontWeight: 600
                    }}>
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      <style>{`
        .btn-hero-primary:hover {
          background: var(--orange-light) !important;
          border-color: var(--orange-light) !important;
        }
        .btn-hero-secondary:hover {
          background: rgba(255,255,255,0.06) !important;
          border-color: rgba(255,255,255,0.4) !important;
        }
      `}</style>
    </section>
  );
};

export default Hero;
